import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ClusterType } from "@/types";

import { Form } from "@/components/Common/Form/Form";
import TextInput from "@/components/forms/inputs/TextInput";
import SelectInput from "@/components/forms/inputs/SelectInput";
import { useToast } from "@/components/ui/use-toast";
import { getAuthContext } from "@/store/auth";
import { trackEvent } from "@/utils/analytics";
import { AnalyticsEventType } from "@/constants/analytics";
import {
  type ContactSalesFormValues,
  MANAGED_CLUSTER_TYPE_OPTIONS,
  clusterTypeToSalesLabel,
  submitContactSalesRequest,
  validateContactSalesForm
} from "@/utils/contactSalesRequest";

type FormFieldName = keyof ContactSalesFormValues;
type FieldErrors = Partial<Record<FormFieldName, string>>;
type FieldTouched = Partial<Record<FormFieldName, boolean>>;

type SetFieldValue = <K extends FormFieldName>(key: K, value: ContactSalesFormValues[K]) => void;

type RequestModalProps = {
  isOpen: boolean;
  onClose: (open: boolean) => void;
  clusterType?: ClusterType;
};

export const RequestClusterModal = ({ isOpen, onClose, clusterType }: RequestModalProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();
  const context = getAuthContext();

  return (
    <Dialog open={isOpen} modal defaultOpen={isOpen} onOpenChange={onClose}>
      <DialogContent className="md:w-[526px] md:max-w-[526px]" showCloseIcon={true}>
        <DialogHeader className="gap-2 py-2">
          <DialogTitle className="title-h5">Request Managed Service</DialogTitle>
        </DialogHeader>
        <Form
          initialValues={{
            firstName: context?.profile?.given_name || "",
            lastName: context?.profile?.family_name || "",
            company: "",
            email: context?.profile?.email || "",
            clusterType: clusterTypeToSalesLabel(clusterType)
          }}
          onSubmit={async (
            values: ContactSalesFormValues,
            {
              resetForm,
              setErrors
            }: {
              resetForm: () => void;
              setErrors: (errors: FieldErrors) => void;
            }
          ) => {
            const fieldErrors = validateContactSalesForm(values);
            if (Object.keys(fieldErrors).length) {
              setErrors(fieldErrors);
              return;
            }
            setIsSubmitting(true);
            try {
              await submitContactSalesRequest(values);
              toast({ title: "Submitted", description: "We'll reach out to you soon." });
              onClose(false);
              resetForm();
            } catch (e) {
              toast({
                title: "Something went wrong",
                description: e instanceof Error ? e.message : "Submission failed",
                variant: "error"
              });
            } finally {
              setIsSubmitting(false);
            }
          }}
          component={({
            values,
            setFieldValue,
            submitForm,
            handleSubmit,
            errors,
            touched,
            setFieldTouched
          }: {
            values: ContactSalesFormValues;
            setFieldValue: SetFieldValue;
            submitForm: () => Promise<void>;
            handleSubmit: (e?: React.FormEvent<HTMLFormElement> | undefined) => void;
            errors: FieldErrors;
            touched: FieldTouched;
            setFieldTouched: (
              field: FormFieldName,
              touched?: boolean,
              shouldValidate?: boolean
            ) => void;
          }) => (
            <form onSubmit={handleSubmit} className="contents">
              <div className="flex w-full flex-col gap-6 border-t border-gray-dark-200 pt-5">
                <div className="subtext-small flex w-fit flex-col text-foreground max-lg:max-w-[270px]">
                  Contact our sales team for managed service access. Select the cluster type
                  you&apos;re interested in and we&apos;ll follow up shortly.
                </div>
              </div>
              <div className="mt-2 flex flex-col gap-3">
                <div className="flex flex-col gap-2">
                  <span className="subtext-small text-gray-dark-2200">First Name</span>
                  <TextInput
                    name="firstName"
                    value={values.firstName}
                    onChange={(v) => setFieldValue("firstName", v)}
                    onBlur={() => setFieldTouched("firstName", true)}
                    error={Boolean(touched.firstName && errors.firstName)}
                    placeholder="Your first name"
                  />
                </div>
                <div className="flex flex-col gap-2">
                  <span className="subtext-small text-gray-dark-2200">Last Name</span>
                  <TextInput
                    name="lastName"
                    value={values.lastName}
                    onChange={(v) => setFieldValue("lastName", v)}
                    onBlur={() => setFieldTouched("lastName", true)}
                    error={Boolean(touched.lastName && errors.lastName)}
                    placeholder="Your last name"
                  />
                </div>
                <div className="flex flex-col gap-2">
                  <span className="subtext-small text-gray-dark-2200">Organization</span>
                  <TextInput
                    name="company"
                    value={values.company}
                    onChange={(v) => setFieldValue("company", v)}
                    onBlur={() => setFieldTouched("company", true)}
                    error={Boolean(touched.company && errors.company)}
                    placeholder="Company / Org"
                  />
                </div>
                <div className="flex flex-col gap-2">
                  <span className="subtext-small text-gray-dark-2200">Email</span>
                  <TextInput
                    name="email"
                    type="email"
                    value={values.email}
                    onChange={(v) => setFieldValue("email", v)}
                    onBlur={() => setFieldTouched("email", true)}
                    error={Boolean(touched.email && errors.email)}
                    placeholder="you@company.com"
                  />
                  <div className="text-2xs text-red-dark-300">{touched.email && errors.email}</div>
                </div>
                <div className="flex flex-col gap-2">
                  <span className="subtext-small text-gray-dark-2200">Cluster Type</span>
                  <SelectInput
                    name="clusterType"
                    value={values.clusterType}
                    onChange={(v) => {
                      setFieldValue("clusterType", v as ContactSalesFormValues["clusterType"]);
                      setFieldTouched("clusterType", true);
                    }}
                    placeholder="Select cluster type"
                    options={MANAGED_CLUSTER_TYPE_OPTIONS}
                    className={`w-full ${touched.clusterType && errors.clusterType ? "border-red-dark-300" : ""}`}
                  />
                  <div className="text-2xs text-red-dark-300">
                    {touched.clusterType && errors.clusterType}
                  </div>
                </div>
              </div>
              <DialogFooter className="pt-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    trackEvent(AnalyticsEventType.REQUEST_CLUSTER_CANCEL_CLICKED);
                    onClose(false);
                  }}
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
                <Button onClick={submitForm} variant="primary" disabled={isSubmitting}>
                  {isSubmitting ? "Submitting..." : "Submit"}
                </Button>
              </DialogFooter>
            </form>
          )}
        />
      </DialogContent>
    </Dialog>
  );
};

export default RequestClusterModal;
